import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import classNames from 'classnames';
import styles from './RunEndOfYearReportsStep.module.scss';
import {Button, Flex, Link, Text} from '@priority-software/priority-style-react';
import {isRTL} from '../../../../../Localization';
import {useFooterContext} from '../../../hooks';
import {Dictionary, NextState} from '../../../types';
import ReportImage from '../../../../../images/EOY-Report-2-image.svg';
import texts from '../../../texts';
import {
    runDelayCheck2Proc,
    runDocDebitSProc,
    runLeasingByYearsProc
} from '../../../actions/runEndOfYearReports';
import {Loader} from '../../../Components';
import {useFiscalYear} from '../../../hooks/useFiscalYear';
import {apploadAttachmentsToLog} from '../../../actions/wizardLog';
import {reportIdByReportName} from '../../../constants';
import {startEntity} from '../../../../../_shared/GWTBridge/invoke/genPage';
import {checkLicense} from '../../../../License/services/licenses';
import {isModuleExists} from '../../../../License/store';

const LOAN_MODULE_NUMBER = 76;

export const RunEndOfYearReportsStep: FC = observer(() => {
    const isRtl = isRTL();
    const footerState = useFooterContext();
    const [reportLinks, setReportLinks] = useState<Dictionary>({});
    const [reportRunning, setReportRunning] = useState<Dictionary<boolean>>({});
    const [fiscalYearStateLoaded, fiscalYearState] = useFiscalYear();
    const [isLoanModuleExists, setIsLoanModuleExists] = useState(false);

    const reports: string[] = useMemo(
        () => [
            ...(isLoanModuleExists ? [texts['stage1_step4_report_0']] : []),
            texts['stage1_step4_report_1'],
            texts['stage1_step4_report_2']
        ],
        [isLoanModuleExists]
    );

    useEffect(() => {
        checkLicense(LOAN_MODULE_NUMBER).then(() => {
            setIsLoanModuleExists(isModuleExists(LOAN_MODULE_NUMBER));
        });
    }, []);

    useEffect(() => {
        setReportLinks(
            reports.reduce(
                (res, current) => ({
                    ...res,
                    [current]: undefined
                }),
                {}
            )
        );
        setReportRunning(
            reports.reduce(
                (res, current) => ({
                    ...res,
                    [current]: false
                }),
                {}
            )
        );
    }, [reports]);

    useEffect(() => {
        if (footerState.onNextState === NextState.Processing) {
            footerState.setOnNextState(NextState.Done);
        }
    }, [footerState.onNextState]);

    const updateReportRunning = useCallback(
        (key: string, value: boolean) => {
            setReportRunning(reportRunning => ({
                ...reportRunning,
                [key]: value
            }));
        },
        [reportRunning, setReportRunning]
    );

    const updateReportLinks = useCallback(
        (key: string, value: string) => {
            setReportLinks(reportLinks => ({
                ...reportLinks,
                [key]: value
            }));
        },
        [reportLinks, setReportLinks]
    );

    const onButtonClickHandler = useCallback(
        (reportName: string) => {
            if (fiscalYearState) {
                footerState.setIsValid(false);
                updateReportRunning(reportName, true);

                const finishedCallback = (url: string) => {
                    updateReportLinks(reportName, url);
                    let reportEname = '';
                    switch (reportName) {
                        case texts['stage1_step4_report_0']:
                            reportEname = 'LOANS_BY_YEARS';
                            break;
                        case texts['stage1_step4_report_1']:
                            reportEname = 'DOC_DEBIT_S';
                            break;
                        case texts['stage1_step4_report_2']:
                            reportEname = 'DELAYCHECK2';
                            break;
                    }
                    apploadAttachmentsToLog(
                        url,
                        fiscalYearState.exttype,
                        fiscalYearState.nscust,
                        reportIdByReportName[reportEname]
                    );
                    footerState.setIsValid(true);
                    updateReportRunning(reportName, false);
                };

                const errorCallback = () => {
                    footerState.setIsValid(true);
                    updateReportRunning(reportName, false);
                };

                switch (reportName) {
                    case texts['stage1_step4_report_0']:
                        runLeasingByYearsProc(fiscalYearState?.prevEndDate, 1)
                            .then(finishedCallback)
                            .catch(errorCallback);
                        break;
                    case texts['stage1_step4_report_1']:
                        runDocDebitSProc(
                            fiscalYearState?.prevStartDate,
                            fiscalYearState?.prevEndDate
                        )
                            .then(finishedCallback)
                            .catch(errorCallback);
                        break;
                    case texts['stage1_step4_report_2']:
                        runDelayCheck2Proc(fiscalYearState?.prevEndDate)
                            .then(finishedCallback)
                            .catch(errorCallback);
                        break;
                }
            }
        },
        [updateReportLinks, updateReportRunning, footerState, fiscalYearState]
    );

    return (
        <Flex
            componentClasses={classNames(styles.wrapper, {
                [styles.wrapperRtl]: isRtl
            })}
            direction={'column'}
        >
            <Text tag={'span'} skin={'paragraph-1'} textColor={'primary-700'}>
                {texts['stage1_step4_explanation']}
            </Text>
            {reports.map(name => {
                const url: string | undefined = reportLinks[name];

                return (
                    <Flex
                        key={name}
                        componentClasses={styles.row}
                        alignItems={'flex-start'}
                        direction={url || reportRunning[name] ? 'column' : 'row'}
                    >
                        <Text tag={'span'} skin={'paragraph-1'} textColor={'primary-700'}>
                            {name}
                        </Text>
                        {!url && !reportRunning[name] ? (
                            <Button
                                skin={'secondary'}
                                size={'medium'}
                                disabled={
                                    Object.values(reportRunning).some(x => !!x) ||
                                    !fiscalYearStateLoaded
                                }
                                onClick={() => onButtonClickHandler(name)}
                            >
                                {texts['stage1_step4_run_and_save']}
                            </Button>
                        ) : (
                            <Flex componentClasses={styles.reportWrapper} direction={'row'}>
                                {reportRunning[name] ? (
                                    <div className={styles.loaderWrapper}>
                                        <Loader
                                            isVisible={reportRunning[name]}
                                            componentClassname={styles.loader}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className={styles.image}>
                                            <img src={ReportImage} alt={''} />
                                        </div>
                                        <Flex alignItems={'flex-start'} direction={'column'}>
                                            <Text
                                                tag={'span'}
                                                skin={'paragraph-1'}
                                                textColor={'primary-700'}
                                            >
                                                {texts['stage_1_step2_links']}
                                                <Link
                                                    target={'_blank'}
                                                    href={url}
                                                    componentClasses={styles.link}
                                                >
                                                    <Text
                                                        tag={'span'}
                                                        skin={'paragraph-1'}
                                                        textColor={'primary-500'}
                                                    >
                                                        {texts['stage_1_step2_link_name']}
                                                    </Text>
                                                </Link>
                                                {texts['stage0_step2_branches_notice_ending']}
                                            </Text>
                                            <Text
                                                tag={'span'}
                                                skin={'paragraph-1'}
                                                textColor={'primary-700'}
                                            >
                                                {texts['stage_1_step2_links2']}
                                                <Link
                                                    componentClasses={styles.link}
                                                    onClick={() => {
                                                        startEntity(
                                                            'EOYLOG',
                                                            'GLNAME',
                                                            fiscalYearState.year.toString(),
                                                            'EXTFILES'
                                                        );
                                                    }}
                                                >
                                                    <Text
                                                        tag={'span'}
                                                        skin={'paragraph-1'}
                                                        textColor={'primary-500'}
                                                    >
                                                        {texts['stage_1_step2_link2_name']}
                                                    </Text>
                                                </Link>
                                                {texts['stage0_step2_branches_notice_ending']}
                                            </Text>
                                        </Flex>
                                    </>
                                )}
                            </Flex>
                        )}
                    </Flex>
                );
            })}
        </Flex>
    );
});
